import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Pause, XCircle, Phone, FileText, MessageSquare } from 'lucide-react';
import { collection, deleteField, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { addDoc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { checkoutPdfBlob } from '../../lib/pdf';
import { useAuth } from '../../lib/auth';
import { sendAlert, sendAlertAwait } from '../../lib/alerts';
import {
  DEMO_AIRCRAFT,
  DEMO_DOCUMENTS,
  DEMO_FLIGHTS,
  DEMO_LESSEES,
  DEMO_SQUAWKS,
  bookingsForLessee,
  txForLessee,
} from '../../lib/demoData';
import { DOCUMENT_TYPE_LABEL, LEASE_DURATION_LABEL, type CheckoutDoc, type UserStatus } from '../../lib/types';
import { LEASE_VERSION } from '../../lib/lease';
import type { BookingDoc, DocumentDoc, FlightDoc, SquawkDoc, TransactionDoc, UserDoc } from '../../lib/types';
import { SQUAWK_SEV_LABEL } from '../../lib/squawks';
import { summarizeCurrentCycle, fmtUSD, fmtHours } from '../../lib/billingCycles';
import { PageHeader } from '../../components/PageHeader';
import { OWNER_FIRST } from '../../lib/identity';
import { TRACKED_REG } from '../../lib/flightTrack';
import { insuranceStatus, fmtPolicyDate } from '../../lib/soloGate';

type Tab = 'overview' | 'profile' | 'documents' | 'flights' | 'bookings' | 'squawks' | 'billing' | 'notes';

const STATUS_BADGE: Record<UserStatus, string> = {
  pending_approval: 'badge-warning',
  profile_incomplete: 'badge-info',
  verification_pending: 'badge-warning',
  active: 'badge-success',
  suspended: 'badge-neutral',
  terminated: 'badge-danger',
};

const STATUS_LABEL: Record<UserStatus, string> = {
  pending_approval: 'Pending approval',
  profile_incomplete: 'Profile incomplete',
  verification_pending: 'Verification pending',
  active: 'Active',
  suspended: 'Suspended',
  terminated: 'Terminated',
};

// Pre-written messages the owner reviews and sends from their own mail app.
// {name} is merged in. mailto keeps Casey in control — he edits + hits send.
const EMAIL_TEMPLATES: { key: string; label: string; subject: string; body: (n: string) => string }[] = [
  {
    key: 'welcome',
    label: 'Welcome',
    subject: 'Welcome to AJM Aviation',
    body: (n) =>
      `Hi ${n},\n\nWelcome aboard — you're all set to fly ${TRACKED_REG}. If you have any questions about booking, billing, or the aircraft, just reply here or call me.\n\nBlue skies,\n${OWNER_FIRST}\nAJM Aviation Ventures`,
  },
  {
    key: 'reupload',
    label: 'Re-upload a document',
    subject: 'Quick fix needed on a document',
    body: (n) =>
      `Hi ${n},\n\nOne of your uploaded documents needs to be re-submitted before I can verify it. Please log into the portal, head to Documents, and upload a clear, current copy.\n\nThanks,\n${OWNER_FIRST}\nAJM Aviation Ventures`,
  },
  {
    key: 'payment',
    label: 'Payment reminder',
    subject: 'Quick note on your account',
    body: (n) =>
      `Hi ${n},\n\nJust a friendly heads-up that there's an outstanding balance on your account. Please make sure your card on file is current in the portal under Billing.\n\nThanks,\n${OWNER_FIRST}\nAJM Aviation Ventures`,
  },
  {
    key: 'checkin',
    label: 'General check-in',
    subject: 'Checking in',
    body: (n) =>
      `Hi ${n},\n\nJust checking in — let me know if you need anything with the aircraft or your account.\n\nCasey\nAJM Aviation Ventures`,
  },
];

const SMS_TEMPLATES: { key: string; label: string; body: (n: string) => string }[] = [
  { key: 'hey', label: 'Quick hello',
    body: (n) => `Hi ${n}, it's ${OWNER_FIRST} with AJM Aviation (${TRACKED_REG}). ` },
  { key: 'callme', label: 'Give me a call',
    body: (n) => `Hi ${n}, it's ${OWNER_FIRST} with AJM Aviation. When you get a minute could you give me a call about ${TRACKED_REG}? Thanks.` },
  { key: 'doc', label: 'Document needed',
    body: (n) => `Hi ${n}, Casey with AJM Aviation. One of your documents needs a fresh copy before I can verify it — you can upload it in the portal under Documents. Thanks!` },
  { key: 'insurance', label: 'Insurance certificate',
    body: (n) => `Hi ${n}, Casey with AJM Aviation. Whenever your renter's policy is set up, send me the certificate of insurance and I'll get it on file. Thanks!` },
  { key: 'grounded', label: 'Aircraft grounded',
    body: (n) => `Hi ${n}, ${OWNER_FIRST} with AJM Aviation. Heads up that ${TRACKED_REG} is grounded right now — please don't fly it until I let you know it's back in service.` },
];

// sms: needs the & before the first param for iOS; Android tolerates it.
function smsFor(phone: string, body: string) {
  return `sms:${phone.replace(/[^0-9+]/g, '')}?&body=${encodeURIComponent(body)}`;
}

function mailtoFor(email: string, subject: string, body: string) {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function AdminLesseeDetail() {
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const { demoMode } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  // This page ran entirely on demo arrays, so opening a real pilot showed sample
  // data or nothing at all. Everything below now reads the live collections;
  // demo mode still falls back to the samples.
  const [liveUser, setLiveUser] = useState<UserDoc | null>(null);
  const [liveDocs, setLiveDocs] = useState<DocumentDoc[] | null>(null);
  const [liveFlights, setLiveFlights] = useState<FlightDoc[] | null>(null);
  const [liveSquawks, setLiveSquawks] = useState<SquawkDoc[] | null>(null);
  const [liveTx, setLiveTx] = useState<TransactionDoc[] | null>(null);
  const [liveBookings, setLiveBookings] = useState<BookingDoc[] | null>(null);
  const [checkouts, setCheckouts] = useState<CheckoutDoc[]>([]);
  const [approving, setApproving] = useState(false);
  const [savingLease, setSavingLease] = useState(false);
  const [savingApproval, setSavingApproval] = useState(false);
  const [approvalBasis, setApprovalBasis] = useState('');

  useEffect(() => {
    if (demoMode || !db || !uid) return;
    const byOwner = (c: string, f: string) => query(collection(db!, c), where(f, '==', uid));
    const unsubs = [
      onSnapshot(doc(db, 'users', uid), (d) => setLiveUser(d.exists() ? ({ ...(d.data() as UserDoc), uid: d.id }) : null)),
      onSnapshot(byOwner('documents', 'ownerUid'), (s) =>
        setLiveDocs(s.docs.map((d) => ({ ...(d.data() as DocumentDoc), documentId: d.id })))),
      onSnapshot(byOwner('flights', 'lesseeUid'), (s) =>
        setLiveFlights(s.docs.map((d) => ({ ...(d.data() as FlightDoc), flightId: d.id })))),
      onSnapshot(byOwner('squawks', 'reportedBy'), (s) =>
        setLiveSquawks(s.docs.map((d) => ({ ...(d.data() as SquawkDoc), squawkId: d.id })))),
      onSnapshot(byOwner('transactions', 'lesseeUid'), (s) =>
        setLiveTx(s.docs.map((d) => ({ ...(d.data() as TransactionDoc), transactionId: d.id })))),
      onSnapshot(byOwner('bookings', 'lesseeUid'), (s) =>
        setLiveBookings(s.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id })))),
      onSnapshot(byOwner('checkouts', 'pilotUid'), (s) =>
        setCheckouts(s.docs.map((d) => ({ ...(d.data() as CheckoutDoc), checkoutId: d.id })))),
    ];
    return () => unsubs.forEach((u) => u());
  }, [uid, demoMode]);

  const lesseeBase = useMemo(
    () => (demoMode ? DEMO_LESSEES.find((u) => u.uid === uid) : liveUser ?? undefined),
    [uid, demoMode, liveUser]
  );
  // Demo mode has no backend, so it needs a local overlay. LIVE mode must never
  // use one: this page used to set the override before the write and regardless
  // of whether it succeeded, so a failed or never-delivered update still showed
  // "Active". On a phone with a weak signal — no offline persistence on this
  // Firestore instance — the write died with the tab and the badge lied. The
  // snapshot is the only thing allowed to say what the status is.
  const [demoStatus, setDemoStatus] = useState<UserStatus | null>(null);
  const lessee = lesseeBase
    ? { ...lesseeBase, status: (demoMode && demoStatus) || lesseeBase.status }
    : undefined;
  const [notes, setNotes] = useState(lesseeBase?.adminNotes ?? '');
  const [statusBusy, setStatusBusy] = useState(false);
  const [sendingReady, setSendingReady] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  /** Returns true only if the change is actually persisted. */
  async function setStatus(status: UserStatus): Promise<boolean> {
    setStatusError(null);
    if (demoMode) {
      setDemoStatus(status);
      return true;
    }
    if (!db || !uid) return false;
    setStatusBusy(true);
    try {
      const extra: Record<string, unknown> = {};
      if (status === 'profile_incomplete') extra.approvedAt = serverTimestamp();
      if (status === 'terminated') extra.terminatedAt = serverTimestamp();
      await updateDoc(doc(db, 'users', uid), { status, ...extra });
      return true;
    } catch (e) {
      console.error('status update failed', e);
      setStatusError("That didn't save — check your connection and try again.");
      return false;
    } finally {
      setStatusBusy(false);
    }
  }
  const approve = () => void setStatus('profile_incomplete');
  const reject = () => {
    if (confirm('Reject this applicant? They will be unable to access the portal.'))
      void setStatus('terminated');
  };
  const activate = async () => {
    const wasInReview = lessee?.status === 'verification_pending';
    // The approval email says the lease "is being prepared and will arrive
    // shortly". Once the signed lease is on file that sentence is false, so the
    // email is suppressed by the fact rather than by a checkbox someone has to
    // remember. Those pilots get the "you're all set" email instead.
    const willEmail = wasInReview && !lessee?.leaseSignedAt && Boolean(lessee?.email);
    if (
      !confirm(
        willEmail
          ? 'Activate this pilot? They get portal access and the "documents approved, ' +
            'lease is next" email. Booking and flight logging stay locked until you record ' +
            'the signed lease.'
          : 'Activate this pilot? They get portal access. No email is sent — their lease ' +
            'is already on file, so send the "you\u2019re all set" email instead.'
      )
    )
      return;
    // Await the write. The pilot must not be told they're approved if the
    // status never actually changed.
    const saved = await setStatus('active');
    if (!saved) return;
    if (willEmail && lessee?.email) {
      sendAlert('pilot_approved', {
        to: lessee.email,
        name: `${lessee.personal?.firstName ?? ''} ${lessee.personal?.lastName ?? ''}`.trim(),
      });
    }
  };
  const suspend = () => {
    if (confirm('Suspend this pilot? They will be blocked from booking and flying.'))
      void setStatus('suspended');
  };
  const terminate = () => {
    if (confirm('Terminate this lease? The pilot loses portal access. This can be reactivated later.'))
      void setStatus('terminated');
  };

  if (!lessee) {
    return (
      <>
        <PageHeader title="Lessee not found" />
        <div className="card p-8 text-center text-ink-muted">
          That lessee doesn’t exist. <Link to="/admin/lessees" className="text-navy-700 underline">Back to list</Link>
        </div>
      </>
    );
  }

  const docs = demoMode ? DEMO_DOCUMENTS.filter((d) => d.ownerUid === uid) : liveDocs ?? [];
  const flights = demoMode ? DEMO_FLIGHTS.filter((f) => f.lesseeUid === uid) : liveFlights ?? [];
  const bookings = demoMode ? bookingsForLessee(uid ?? '') : liveBookings ?? [];
  const squawks = demoMode ? DEMO_SQUAWKS.filter((s) => s.reportedBy === uid) : liveSquawks ?? [];
  const tx = demoMode ? txForLessee(uid ?? '') : liveTx ?? [];

  const aircraft = DEMO_AIRCRAFT[0];
  // PB214's *Pilots Not Named* reads "any Student or Renter pilot approved by the
  // Named Insured" and its required-training box is BLANK — so the owner's approval
  // is the entire carrier test, and a CFI checkout is AJM's own rule (lease §4.3(d)),
  // not Old Republic's. This records that approval on its own, without waiting for a
  // checkout doc to exist. Approving from a submitted checkout still works and
  // additionally files the signed PDF.
  // The final email of onboarding. Gated on the executed lease being recorded and
  // the pilot being active, because that is exactly what it tells them. The write
  // is awaited BEFORE the send so a failure can't leave "sent" showing on screen
  // with nothing delivered — the same trap the status badge used to fall into.
  const sendReadyEmail = async () => {
    if (!lessee?.email || sendingReady) return;
    if (
      !confirm(
        `Send the "you're all set" email to ${lessee.email}?\n\n` +
        'It tells them onboarding is complete and the last step is meeting you at the ' +
        'airplane for the walkthrough, the ground rules and their key.'
      )
    )
      return;
    setSendingReady(true);
    try {
      const sent = await sendAlertAwait('pilot_ready', {
        to: lessee.email,
        name: `${lessee.personal?.firstName ?? ''} ${lessee.personal?.lastName ?? ''}`.trim(),
      });
      if (!sent) throw new Error('send failed');
      if (!demoMode && db && uid) {
        await updateDoc(doc(db, 'users', uid), { readyEmailSentAt: serverTimestamp() });
      }
    } catch (e) {
      console.error('sendReadyEmail', e);
      alert("That didn't send. Check your connection and try again.");
    } finally {
      setSendingReady(false);
    }
  };

  const setRenterApproval = async (approved: boolean) => {
    if (!db || !uid || savingApproval) return;
    setSavingApproval(true);
    try {
      await updateDoc(doc(db, 'users', uid), {
        renterApprovalAt: approved ? serverTimestamp() : null,
        renterApprovalBasis: approved ? approvalBasis.trim() || null : null,
      });
      setApprovalBasis('');
    } catch (e) {
      console.error('setRenterApproval', e);
      alert('Could not record the approval — check the console and try again.');
    } finally {
      setSavingApproval(false);
    }
  };

  // The executed lease is the booking gate (see lib/bookingRules.ts). Nothing writes
  // this automatically: signing moved to DocuSign on 2026-07-13, so the owner flips it
  // once the completed envelope — Casey's countersignature included — is back.
  const setLeaseSigned = async (signed: boolean) => {
    if (!db || !uid || savingLease) return;
    setSavingLease(true);
    try {
      await updateDoc(doc(db, 'users', uid), {
        leaseSignedAt: signed ? serverTimestamp() : null,
        leaseSignedVersion: signed ? LEASE_VERSION : null,
      });
    } catch (e) {
      console.error('setLeaseSigned', e);
      alert('Could not update the lease status — check the console and try again.');
    } finally {
      setSavingLease(false);
    }
  };

  const approveCheckout = async (c: CheckoutDoc) => {
    if (!db || !storage || !uid || approving) return;
    setApproving(true);
    try {
      const now = new Date();
      const approvedAtText = now.toLocaleString();
      await updateDoc(doc(db, 'checkouts', c.checkoutId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: 'owner',
      });
      await updateDoc(doc(db, 'users', uid), { renterApprovalAt: serverTimestamp() });
      // File the evidence where the rest of the pilot's paper lives.
      const fresh = await getDoc(doc(db, 'checkouts', c.checkoutId));
      const blob = checkoutPdfBlob({ ...(fresh.data() as CheckoutDoc), checkoutId: c.checkoutId }, approvedAtText);
      const path = `documents/${uid}/${Date.now()}-checkout-signoff.pdf`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob, { contentType: 'application/pdf' });
      const url = await getDownloadURL(sref);
      const dref = await addDoc(collection(db, 'documents'), {
        ownerUid: uid,
        type: 'checkout',
        label: `CFI checkout — ${c.cfiName}, ${c.date}`,
        fileRef: url,
        fileName: 'checkout-signoff.pdf',
        verificationStatus: 'verified',
        verifiedAt: serverTimestamp(),
        verifiedBy: 'owner',
        uploadedAt: serverTimestamp(),
      });
      await updateDoc(dref, { documentId: dref.id });
    } catch (e) {
      console.error('approveCheckout', e);
      alert('Approval failed — check the console and try again.');
    } finally {
      setApproving(false);
    }
  };

  const cycle = summarizeCurrentCycle({ flights, aircraft });
  const lifetime = tx.reduce((s, t) => s + t.amount, 0);

  return (
    <>
      <PageHeader
        title={`${lessee.personal?.firstName ?? ''} ${lessee.personal?.lastName ?? ''}`.trim()}
        subtitle={lessee.email}
        actions={
          <div className="flex gap-2">
            <button onClick={() => nav('/admin/lessees')} className="btn-ghost text-sm flex items-center gap-1">
              <ArrowLeft size={14} /> All lessees
            </button>
            <span className={STATUS_BADGE[lessee.status]}>{STATUS_LABEL[lessee.status]}</span>
          </div>
        }
      />

      {/* The last step of onboarding, promoted to the top of the page. It used to
          live as the fifth card down — roughly 1300px on a phone — which meant the
          one action that finishes a pilot was the hardest thing on the page to find. */}
      {lessee.leaseSignedAt && lessee.status === 'active' && !lessee.readyEmailSentAt && !lessee.welcomeEmailWaivedAt && (
        <div className="card mb-4 border-l-4 border-l-navy-900 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="mr-auto min-w-0">
              <div className="font-display text-base font-semibold text-navy-900">
                Ready to finish onboarding
              </div>
              <div className="mt-0.5 text-sm text-ink-muted">
                Lease executed and account active. Send the welcome email — it asks them to set up
                the walkthrough and pick up their key.
              </div>
            </div>
            <button
              type="button"
              disabled={sendingReady}
              className="btn-primary min-h-0 shrink-0 px-4 py-2 text-sm"
              onClick={sendReadyEmail}
            >
              {sendingReady ? 'Sending…' : "Send \u201cyou're all set\u201d email"}
            </button>
          </div>
        </div>
      )}

      {/* Approve / deny — manage this pilot's access right from their info page. */}
      <div className="card mb-6 flex flex-wrap items-center gap-2 p-4">
        <div className="mr-auto text-sm">
          <span className="text-ink-muted">Status: </span>
          <span className="font-medium text-navy-900">{STATUS_LABEL[lessee.status]}</span>
        </div>
        {lessee.status === 'pending_approval' && (
          <>
            <button onClick={approve} disabled={statusBusy} className="btn-primary py-2 px-4 text-sm">Approve</button>
            <button onClick={reject} disabled={statusBusy} className="btn-danger py-2 px-4 text-sm">Reject</button>
          </>
        )}
        {lessee.status === 'verification_pending' && (
          <button onClick={activate} disabled={statusBusy} className="btn-primary py-2 px-4 text-sm">
            {statusBusy ? 'Activating…' : 'Activate pilot'}
          </button>
        )}
        {lessee.status === 'active' && (
          <>
            {/* Retro-log a flight this lessee flew but forgot to log — writes it to
                their account and charges their card, same as a self-logged flight. */}
            <Link to={`/admin/log-flight?for=${uid}`} className="btn-primary py-2 px-4 text-sm">
              Log a flight for them
            </Link>
            <button onClick={suspend} className="btn-secondary py-2 px-4 text-sm">Suspend</button>
          </>
        )}
        {(lessee.status === 'suspended' || lessee.status === 'terminated') && (
          <button onClick={activate} disabled={statusBusy} className="btn-primary py-2 px-4 text-sm">
            {statusBusy ? 'Saving…' : 'Reactivate'}
          </button>
        )}
        {lessee.status === 'profile_incomplete' && (
          <span className="text-sm text-ink-muted">Approved — waiting on the pilot to finish onboarding.</span>
        )}
        {/* A status change that didn't save has to say so. This page used to show
            the new status regardless and leave the failure in the console. */}
        {statusError && (
          <span className="w-full text-sm font-medium text-[#B42318]">{statusError}</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1 mb-6 border-b border-navy-900/10">
        {([
          ['overview', 'Overview'],
          ['profile', 'Profile'],
          ['documents', `Documents (${docs.length})`],
          ['flights', `Flights (${flights.length})`],
          ['bookings', `Bookings (${bookings.length})`],
          ['squawks', `Squawks (${squawks.length})`],
          ['billing', 'Billing'],
          ['notes', 'Private notes'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition ${
              tab === key
                ? 'border-brand text-navy-900'
                : 'border-transparent text-ink-muted hover:text-navy-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── CFI checkout / Named-Insured approval ──
          The Policy's open-pilot clause covers "any Student or Renter pilot approved by
          the Named Insured". The CFI's sign-off is the evidence; this button is the
          approval itself. Two acts, two timestamps, on purpose. Approving also files
          the sign-off as a verified PDF in the pilot's Documents. */}
      {tab === 'overview' && checkouts.length > 0 && (
        <div className="mb-6 card p-5">
          <h3 className="font-display text-base font-semibold text-navy-900 mb-3">CFI checkout</h3>
          <div className="space-y-3">
            {[...checkouts]
              .sort((a, b) => (b.signedAt?.toMillis?.() ?? 0) - (a.signedAt?.toMillis?.() ?? 0))
              .map((c) => (
                <div key={c.checkoutId} className="rounded-lg border border-slate-200 p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-medium text-navy-900">{c.cfiName}</span>
                      <span className="text-ink-muted"> · {c.date} · {c.dualHoursGiven.toFixed(1)} hr dual · CFI #{c.cfiCertNumber}</span>
                      <div className="mt-1 text-xs font-medium text-navy-700">
                        Privileges: {(c.privileges ?? ['vfr_day']).map((p) => ({ vfr_day: 'Day VFR', night: 'Night', ifr: 'IFR' })[p]).join(' · ')}
                        {c.pilotCertificate ? ` — holds ${c.pilotCertificate}` : ''}
                      </div>
                      {c.notes && <div className="mt-1 text-ink-muted">“{c.notes}”</div>}
                    </div>
                    {c.status === 'approved' ? (
                      <span className="badge-success">
                        Approved as renter pilot{c.approvedAt ? ` · ${c.approvedAt.toDate().toLocaleDateString()}` : ''}
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={approving}
                        className="btn-primary text-sm"
                        onClick={() => approveCheckout(c)}
                      >
                        {approving ? 'Approving…' : 'Approve as renter pilot'}
                      </button>
                    )}
                  </div>
                  {c.status !== 'approved' && (
                    <p className="mt-2 text-xs text-ink-muted">
                      This records your approval as Named Insured under the policy&rsquo;s open-pilot
                      provisions and files the signed checkout into their Documents.
                    </p>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {lessee.role === 'lessee' && lessee.billingProfile !== 'owner' && (() => {
            const ins = insuranceStatus(lessee);
            const i = lessee.insurance;
            const tone =
              ins.state === 'expired'
                ? 'border-l-4 border-[#B42318]'
                : ins.state === 'expiring'
                ? 'border-l-4 border-[#B54708]'
                : ins.state === 'none'
                ? 'border-l-4 border-slate-300'
                : 'border-l-4 border-emerald-500';
            const headline =
              ins.state === 'expired'
                ? `EXPIRED ${fmtPolicyDate(ins.exp)} — ${Math.abs(ins.days ?? 0)} day${Math.abs(ins.days ?? 0) === 1 ? '' : 's'} ago`
                : ins.state === 'expiring'
                ? `Expires ${fmtPolicyDate(ins.exp)} — ${ins.days === 0 ? 'today' : `${ins.days} day${ins.days === 1 ? '' : 's'} left`}`
                : ins.state === 'none'
                ? 'No renter’s policy on file'
                : `Current through ${fmtPolicyDate(ins.exp)}`;
            const color =
              ins.state === 'expired' ? 'text-[#B42318]' : ins.state === 'expiring' ? 'text-[#B54708]' : ins.state === 'none' ? 'text-ink-muted' : 'text-emerald-700';
            return (
              <div className={`lg:col-span-3 card p-5 ${tone}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-display text-base font-semibold text-navy-900">Renter’s insurance</h3>
                  <span className={`text-sm font-semibold ${color}`}>{headline}</span>
                </div>
                {ins.state === 'none' ? (
                  <p className="mt-2 text-sm text-ink-muted">
                    Lease §11.2 requires a non-owned (renter’s) policy before any flight after the first 14 days, and an
                    updated certificate on each renewal.
                  </p>
                ) : (
                  <>
                    <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
                      <Stat label="Carrier" value={i?.carrier || '—'} />
                      <Stat label="Policy no." value={i?.policyNumber || '—'} />
                      <Stat label="Liability" value={i?.liabilityLimit ? `$${i.liabilityLimit.toLocaleString()} / occurrence` : '—'} />
                      <Stat label="Per passenger" value={i?.passengerLimit ? `$${i.passengerLimit.toLocaleString()}` : '—'} />
                      <Stat label="Aircraft damage" value={i?.aircraftDamageLimit ? `$${i.aircraftDamageLimit.toLocaleString()}` : '—'} />
                      <Stat label="Term" value={`${i?.effectiveDate ? fmtPolicyDate(i.effectiveDate) : '—'} → ${fmtPolicyDate(ins.exp)}`} />
                    </div>
                    {i?.notes && <p className="mt-3 text-xs leading-snug text-ink-muted">{i.notes}</p>}
                    {ins.state === 'expired' && (
                      <p className="mt-3 text-sm text-[#B42318]">
                        {lessee.billingProfile === 'cfi'
                          ? 'This instructor has no renter’s coverage in force.'
                          : `No solo pilot in command in ${TRACKED_REG} until a renewed certificate is on file (§11.2).`}
                      </p>
                    )}
                  </>
                )}
                <PolicyNamedEditor lessee={lessee} demoMode={demoMode} />
              </div>
            );
          })()}
          <div className="lg:col-span-2 card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Contact</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Phone" value={lessee.personal?.phone ?? '—'} />
              <Stat label="Address" value={lessee.personal?.address ?? '—'} />
              <Stat label="Joined" value={lessee.createdAt?.toDate().toLocaleDateString() ?? '—'} />
              <Stat label="Approved" value={lessee.approvedAt?.toDate().toLocaleDateString() ?? '—'} />
            </div>
          </div>
          <div className="card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-1">
              Renter pilot approval
            </h3>
            <p className="mb-3 text-xs text-ink-muted">
              Old Republic asks only that the pilot be “approved by the Named Insured.” That is
              you. No checkout is required by the carrier.
            </p>
            {lessee.renterApprovalAt ? (
              <>
                <p className="text-sm font-medium text-emerald-700">
                  Approved &middot; {lessee.renterApprovalAt.toDate().toLocaleDateString()}
                </p>
                {lessee.renterApprovalBasis && (
                  <p className="mt-1 text-xs leading-snug text-ink-muted">
                    {lessee.renterApprovalBasis}
                  </p>
                )}
                <button
                  type="button"
                  disabled={savingApproval}
                  className="btn-secondary mt-3 text-sm"
                  onClick={() => setRenterApproval(false)}
                >
                  {savingApproval ? 'Saving…' : 'Withdraw approval'}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-amber-800">
                  Not approved. Without your approval this pilot is outside the policy’s
                  open-pilot provisions.
                </p>
                <input
                  className="input mt-3 text-sm"
                  placeholder="What it rests on — e.g. flew with him 8/27"
                  value={approvalBasis}
                  onChange={(e) => setApprovalBasis(e.target.value)}
                />
                <button
                  type="button"
                  disabled={savingApproval}
                  className="btn-primary mt-2 text-sm"
                  onClick={() => setRenterApproval(true)}
                >
                  {savingApproval ? 'Saving…' : 'Approve as renter pilot'}
                </button>
              </>
            )}
          </div>
          <div className="card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Signed lease</h3>
            {lessee.leaseSignedAt ? (
              <>
                <p className="text-sm text-emerald-700 font-medium">
                  On file &middot; {lessee.leaseSignedAt.toDate().toLocaleDateString()}
                </p>
                {lessee.leaseSignedVersion && (
                  <p className="mt-1 text-xs text-ink-muted">{lessee.leaseSignedVersion}</p>
                )}
                <button
                  type="button"
                  disabled={savingLease}
                  className="btn-secondary mt-3 text-sm"
                  onClick={() => setLeaseSigned(false)}
                >
                  {savingLease ? 'Saving…' : 'Clear'}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-amber-800">
                  Not on file. This pilot <b>cannot book</b> until the countersigned lease is recorded.
                </p>
                <button
                  type="button"
                  disabled={savingLease}
                  className="btn-primary mt-3 text-sm"
                  onClick={() => setLeaseSigned(true)}
                >
                  {savingLease ? 'Saving…' : 'Mark lease signed'}
                </button>
                <p className="mt-2 text-xs text-ink-muted">
                  Records today against {LEASE_VERSION}. Do this only once the completed
                  DocuSign envelope is back, your countersignature included.
                </p>
              </>
            )}
          </div>
          <div className="card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Finish onboarding</h3>
            {lessee.readyEmailSentAt ? (
              <>
                <p className="text-sm font-medium text-emerald-700">
                  Sent &middot; {lessee.readyEmailSentAt.toDate().toLocaleDateString()}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  They've been asked to set up the walkthrough and pick up their key.
                </p>
                <button
                  type="button"
                  disabled={sendingReady}
                  className="btn-secondary mt-3 text-sm"
                  onClick={sendReadyEmail}
                >
                  {sendingReady ? 'Sending…' : 'Send again'}
                </button>
              </>
            ) : (
              <p className="text-sm text-ink-muted">
                {!lessee.leaseSignedAt && !(lessee.status === 'active')
                  ? 'Waiting on the signed lease and activation.'
                  : !lessee.leaseSignedAt
                    ? 'Waiting on the signed lease.'
                    : lessee.status !== 'active'
                      ? 'Waiting on activation.'
                      : lessee.welcomeEmailWaivedAt
                        ? 'Onboarded before this email existed — walked through in person.'
                        : 'Ready — use the button at the top of this page.'}
              </p>
            )}
          </div>
          <div className="card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Quick stats</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-ink-muted">Card on file</span>
                {lessee.paymentMethodOnFile ? (
                  <span className="font-medium text-emerald-700">
                    {lessee.cardBrand && lessee.cardLast4
                      ? `${lessee.cardBrand.charAt(0).toUpperCase()}${lessee.cardBrand.slice(1)} ····${lessee.cardLast4}`
                      : 'Yes'}
                  </span>
                ) : (
                  <span className="font-medium text-red-700">None</span>
                )}
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-muted">Flights logged</span>
                <span className="font-medium">{flights.length}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-muted">Hours flown</span>
                <span className="font-medium">{fmtHours(flights.reduce((s, f) => s + f.hoursFlown, 0))}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-muted">Open squawks</span>
                <span className="font-medium">{squawks.filter((s) => s.status !== 'resolved').length}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-muted">Lifetime revenue</span>
                <span className="font-medium">{fmtUSD(lifetime)}</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3 card p-5">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-1">Send a message</h3>
            <p className="mb-3 text-sm text-ink-muted">
              Pick a template — it opens in your mail app pre-filled for {lessee.personal?.firstName || 'this pilot'}. Review and hit send.
            </p>
            <div className="mb-5 flex flex-wrap gap-2">
              {EMAIL_TEMPLATES.map((t) => (
                <a
                  key={t.key}
                  href={mailtoFor(lessee.email, t.subject, t.body(lessee.personal?.firstName || 'there'))}
                  className="btn-secondary text-sm"
                >
                  {t.label}
                </a>
              ))}
              <a
                href={`mailto:${lessee.email}?subject=${encodeURIComponent(`AJM Aviation — ${TRACKED_REG}`)}`}
                className="btn-secondary text-sm flex items-center gap-1.5"
              >
                <Send size={14} /> Blank email
              </a>
            </div>

            {lessee.personal?.phone && (
              <>
                <h3 className="font-display text-base font-semibold text-navy-900 mb-1">
                  Text {lessee.personal.firstName || 'this pilot'}
                </h3>
                <p className="mb-3 text-sm text-ink-muted">
                  Opens Messages pre-filled to {lessee.personal.phone}. Each one says who it's from, in case they
                  don't have your number saved either.
                </p>
                <div className="mb-5 flex flex-wrap gap-2">
                  {SMS_TEMPLATES.map((t) => (
                    <a
                      key={t.key}
                      href={smsFor(lessee.personal!.phone!, t.body(lessee.personal?.firstName || 'there'))}
                      className="btn-secondary text-sm"
                    >
                      {t.label}
                    </a>
                  ))}
                  <a
                    href={smsFor(lessee.personal.phone, '')}
                    className="btn-secondary text-sm flex items-center gap-1.5"
                  >
                    <MessageSquare size={14} /> Blank text
                  </a>
                </div>
              </>
            )}

            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Owner actions</h3>
            <div className="flex flex-wrap gap-2">
              {lessee.personal?.phone && (
                <a
                  href={`tel:${lessee.personal.phone.replace(/[^0-9+]/g, '')}`}
                  className="btn-secondary text-sm flex items-center gap-1.5"
                >
                  <Phone size={14} /> Call {lessee.personal.phone}
                </a>
              )}
              {lessee.status === 'active' && (
                <button onClick={suspend} className="btn-secondary text-sm flex items-center gap-1.5">
                  <Pause size={14} /> Suspend account
                </button>
              )}
              {lessee.status === 'suspended' && (
                <button onClick={activate} className="btn-secondary text-sm flex items-center gap-1.5">
                  <Send size={14} /> Reactivate account
                </button>
              )}
              {lessee.status !== 'terminated' && (
                <button onClick={terminate} className="btn-danger text-sm flex items-center gap-1.5">
                  <XCircle size={14} /> Terminate lease
                </button>
              )}
            </div>
            <p className="mt-3 text-xs text-ink-muted">
              Custom one-off charges run through the billing system — tell me if you want that wired up.
            </p>
          </div>
        </div>
      )}

      {tab === 'profile' && (
        <div className="card p-5">
          {lessee.pilot ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Stat label="Certificate" value={`${lessee.pilot.certificateType ?? '—'} · ${lessee.pilot.certificateNumber ?? ''}`} />
              <Stat label="Ratings" value={(lessee.pilot.ratings ?? []).join(', ') || '—'} />
              <Stat label="Total hours" value={String(lessee.pilot.totalHours ?? 0)} />
              <Stat label="Hours in C172" value={String(lessee.pilot.hoursIn172 ?? 0)} />
              <Stat label="Last 90 days" value={String(lessee.pilot.hoursLast90 ?? 0)} />
              <Stat label="Last 12 months" value={String(lessee.pilot.hoursLast12Months ?? 0)} />
              <Stat label="Medical" value={`${lessee.pilot.medicalClass ?? '—'} · exp ${lessee.pilot.medicalExpiration ?? '—'}`} />
              <Stat label="BFR expiration" value={lessee.pilot.bfrExpiration ?? '—'} />
              <Stat
                label="Renter’s insurance"
                value={(() => {
                  const ins = insuranceStatus(lessee);
                  if (ins.state === 'none') return 'None on file';
                  return `${lessee.insurance?.carrier} #${lessee.insurance?.policyNumber} · ${
                    ins.state === 'expired' ? `EXPIRED ${fmtPolicyDate(ins.exp)}` : `exp ${fmtPolicyDate(ins.exp)}`
                  }`;
                })()}
              />
              <Stat label="Planned lease length" value={LEASE_DURATION_LABEL[lessee.pilot.intendedLeaseDuration ?? ''] ?? '—'} />
              <Stat label="Working toward" value={lessee.pilot.flyingGoal || '—'} />
            </div>
          ) : (
            <div className="text-sm text-ink-muted">Profile not submitted yet.</div>
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sky-50 text-left text-ink-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {docs.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-ink-muted">No documents uploaded.</td></tr>
              ) : (
                docs.map((d) => (
                  <tr key={d.documentId}>
                    <td className="px-4 py-3">
                      {d.fileRef ? (
                        <a
                          href={d.fileRef}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 font-medium text-navy-700 hover:underline"
                        >
                          <FileText size={14} /> {DOCUMENT_TYPE_LABEL[d.type]}
                        </a>
                      ) : (
                        DOCUMENT_TYPE_LABEL[d.type]
                      )}
                      {d.fileName && <div className="mt-0.5 text-xs text-ink-subtle">{d.fileName}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={d.verificationStatus === 'verified' ? 'badge-success' : d.verificationStatus === 'rejected' ? 'badge-danger' : 'badge-warning'}>
                        {d.verificationStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">{d.expirationDate || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'flights' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sky-50 text-left text-ink-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Route</th>
                <th className="px-4 py-3 font-medium">Hours</th>
                <th className="px-4 py-3 font-medium text-right">Charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {flights.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-ink-muted">No flights logged.</td></tr>
              ) : (
                flights.map((f) => (
                  <tr key={f.flightId}>
                    <td className="px-4 py-3">{f.date}</td>
                    <td className="px-4 py-3">{f.departureAirport} → {f.destinations.join(' → ')}</td>
                    <td className="px-4 py-3">{f.hoursFlown.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right">{fmtUSD(f.totalCharge)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sky-50 text-left text-ink-muted">
              <tr>
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">Route</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bookings.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-ink-muted">No bookings.</td></tr>
              ) : (
                bookings.map((b) => (
                  <tr key={b.bookingId}>
                    <td className="px-4 py-3">{b.startDateTime.toDate().toLocaleString()}</td>
                    <td className="px-4 py-3">{b.expectedDestinations}</td>
                    <td className="px-4 py-3">
                      <span className={b.status === 'pending_approval' ? 'badge-warning' : 'badge-success'}>
                        {b.status.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'squawks' && (
        <div className="card p-5">
          {squawks.length === 0 ? (
            <div className="text-sm text-ink-muted">No squawks reported by this lessee.</div>
          ) : (
            <ul className="space-y-3 text-sm">
              {squawks.map((s) => (
                <li key={s.squawkId} className="border-l-2 border-amber-400 pl-3">
                  <div className="text-xs text-ink-muted">{SQUAWK_SEV_LABEL[s.severity]} · {s.dateDiscovered}</div>
                  <div>{s.description}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'billing' && (
        <div className="card p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="This cycle hours" value={fmtHours(cycle.hoursFlown)} />
            <Stat label="This cycle charges" value={fmtUSD(cycle.charges)} />
            <Stat label="Nights away billed" value={fmtUSD(cycle.awayCharge)} />
            <Stat label="Lifetime revenue" value={fmtUSD(lifetime)} />
          </div>
          <div className="mt-6">
            <h3 className="font-display text-base font-semibold text-navy-900 mb-3">Transactions</h3>
            {tx.length === 0 ? (
              <div className="text-sm text-ink-muted">No transactions yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {tx.map((t) => (
                  <li key={t.transactionId} className="py-2 flex items-center justify-between">
                    <div>
                      <div>{t.createdAt.toDate().toLocaleDateString()} · {t.type.replace('_', ' ')}</div>
                      <div className="text-xs text-ink-muted font-mono">{t.stripeChargeId}</div>
                    </div>
                    <div className="font-medium">{fmtUSD(t.amount)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'notes' && (
        <div className="card p-5">
          <h3 className="font-display text-base font-semibold text-navy-900 mb-2">Private notes</h3>
          <p className="text-xs text-ink-muted mb-3">
            Visible only to admin. Use for relationship context — referrals, preferences, special arrangements.
          </p>
          <textarea
            className="input-textarea min-h-[160px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="met through John Smith referral · prefers weekday mornings · always returns the plane spotless"
          />
          <div className="mt-3 flex justify-end">
            <button className="btn-primary text-sm">Save notes</button>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="text-sm text-ink mt-0.5">{value}</div>
    </div>
  );
}

// "Named on the aircraft policy" — the insurer's endorsement adding this
// person as a named pilot and/or approved instructor. Recorded here by the
// owner when the carrier confirms; shown on the Lessees card. It never
// stands in for renter's insurance, which §11.2 still requires.
function PolicyNamedEditor({ lessee, demoMode }: { lessee: UserDoc; demoMode: boolean }) {
  const cur = lessee.policyNamed;
  const [open, setOpen] = useState(false);
  const [as, setAs] = useState<'' | 'pilot' | 'instructor' | 'both'>(cur?.as ?? '');
  const [since, setSince] = useState(cur?.since ?? '');
  const [policyNumber, setPolicyNumber] = useState(cur?.policyNumber ?? '');
  const [through, setThrough] = useState(cur?.through ?? '');
  const [notes, setNotes] = useState(cur?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const label = (a: string) => (a === 'both' ? 'pilot & instructor' : a);

  async function save() {
    if (demoMode || !db) { setOpen(false); return; }
    setBusy(true);
    setErr('');
    try {
      await updateDoc(doc(db, 'users', lessee.uid), {
        policyNamed: as
          ? {
              as,
              ...(since ? { since } : {}),
              ...(policyNumber.trim() ? { policyNumber: policyNumber.trim() } : {}),
              ...(through ? { through } : {}),
              ...(notes.trim() ? { notes: notes.trim() } : {}),
            }
          : deleteField(),
      });
      setOpen(false);
    } catch {
      setErr('That didn’t save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-slate-200/70 pt-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-semibold text-navy-900">Aircraft policy: </span>
          {cur ? (
            <span className="text-ink">
              named {label(cur.as)}
              {cur.since ? ` since ${fmtPolicyDate(cur.since)}` : ''}
              {cur.policyNumber ? ` · ${cur.policyNumber}` : ''}
              {cur.through ? ` · through ${fmtPolicyDate(cur.through)}` : ''}
            </span>
          ) : (
            <span className="text-ink-muted">not named on the aircraft’s policy</span>
          )}
          <span className="block text-xs text-ink-muted">Renter’s insurance is still required (§11.2).</span>
          {cur?.notes && <span className="block text-xs text-ink-muted">{cur.notes}</span>}
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-sm font-medium text-navy-700 underline">
          {open ? 'Cancel' : cur ? 'Edit' : 'Record endorsement'}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="label">Named as</span>
            <select className="input mt-1" value={as} onChange={(e) => setAs(e.target.value as typeof as)}>
              <option value="">Not named</option>
              <option value="pilot">Named pilot</option>
              <option value="instructor">Approved instructor</option>
              <option value="both">Named pilot & instructor</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Effective</span>
            <input type="date" className="input mt-1" value={since} onChange={(e) => setSince(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Policy no.</span>
            <input className="input mt-1" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder="PB …" />
          </label>
          <label className="block">
            <span className="label">Policy term ends</span>
            <input type="date" className="input mt-1" value={through} onChange={(e) => setThrough(e.target.value)} />
          </label>
          <label className="block sm:col-span-2 lg:col-span-4">
            <span className="label">Notes</span>
            <input className="input mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. no additional premium; endorsement PDF in attachments" />
          </label>
          <div className="flex items-center gap-3 sm:col-span-2 lg:col-span-4">
            <button type="button" onClick={save} disabled={busy} className="btn-primary text-sm">
              {busy ? 'Saving…' : 'Save'}
            </button>
            {err && <span className="text-red-700">{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
